import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import {
  FolderOpen, Download, CheckCircle, AlertCircle, Loader2,
  RotateCw, RotateCcw, ChevronLeft, Upload as UploadIcon, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import type { RotationSettings } from './RotationControls';
import { CastingVideoImporter as _CastingVideoImporter } from './CastingVideoImporter';
import LibraryPicker, { stagedFileName, type StagedLibraryFile } from './library/LibraryPicker';
import { UpgradeModal } from './subscription';

interface VideoFile {
  id: string;
  file: File;
  status: 'ready' | 'uploading' | 'uploaded' | 'processing' | 'processed' | 'error';
  progress: number;
  statusText?: string;
  downloadUrl?: string;
  s3Key?: string;
  isImported?: boolean; // true if imported from casting (already in S3)
}

interface ImportedVideo {
  s3Key: string;
  s3Url: string;
  castingData: {
    talentName: string;
    takeNumber: number;
  };
}

// File size limit for non-subscribers (10MB)
const MAX_FILE_SIZE_FREE = 10 * 1024 * 1024;

export const EnhancedVideoRotator = () => {
  const [files, setFiles] = useState<VideoFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rotationSettings, setRotationSettings] = useState<RotationSettings>({
    direction: 'clockwise',
    angle: 90
  });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const { toast } = useToast();
  const { isSubscriber } = useSubscription();
  const { getAuthHeaders } = useAuth();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const videoFiles = acceptedFiles.filter(file => file.type.startsWith('video/'));

    if (videoFiles.length === 0) {
      toast({
        title: "Invalid files",
        description: "Please select video files only",
        variant: "destructive"
      });
      return;
    }

    // Check file size limit for non-subscribers - show upgrade modal
    if (!isSubscriber) {
      const oversizedFiles = videoFiles.filter(file => file.size > MAX_FILE_SIZE_FREE);
      if (oversizedFiles.length > 0) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const newFiles: VideoFile[] = videoFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: 'ready',
      progress: 0,
      statusText: 'Ready to upload'
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, [toast, isSubscriber]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
    }
  });

  const updateFileState = (id: string, updates: Partial<VideoFile>) => {
    setFiles(prev => prev.map(file => 
      file.id === id ? { ...file, ...updates } : file
    ));
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(file => file.id !== id));
  };

  const clearAll = () => {
    setFiles([]);
    setIsProcessing(false);
  };

  // "Choose from your library" — the staged key already lives in the rotator
  // bucket, so it rides the existing isImported skip-upload path.
  const handleLibraryPick = (f: StagedLibraryFile) => {
    const newFile: VideoFile = {
      id: Math.random().toString(36).substr(2, 9),
      file: new File([], stagedFileName(f), { type: 'video/mp4' }),
      status: 'uploaded',
      progress: 100,
      statusText: 'From your library — ready to rotate',
      s3Key: f.key,
      isImported: true,
    };
    setFiles(prev => [...prev, newFile]);
  };

  const handleVideosImported = (importedVideos: ImportedVideo[]) => {
    // Convert imported videos to VideoFile format
    // These videos are already in S3, so they can skip upload and go straight to processing
    const newFiles: VideoFile[] = importedVideos.map((video) => {
      const fileName = video.s3Key.split('/').pop() || `${video.castingData.talentName}_take${video.castingData.takeNumber}.mp4`;
      // Create a minimal File object for display purposes
      const dummyFile = new File([], fileName, { type: 'video/mp4' });

      return {
        id: Math.random().toString(36).substr(2, 9),
        file: dummyFile,
        status: 'uploaded' as const, // Already in S3, ready for processing
        progress: 100,
        statusText: 'Imported from casting - ready to rotate',
        s3Key: video.s3Key,
        isImported: true
      };
    });

    setFiles(prev => [...prev, ...newFiles]);

    toast({
      title: "Videos imported",
      description: `Added ${newFiles.length} video${newFiles.length !== 1 ? 's' : ''} from casting`
    });
  };

  const uploadToS3 = async (file: File, uploadUrl: string, onProgress: (progress: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });
      
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  const startProcessing = async () => {
    setIsProcessing(true);
    
    try {
      toast({
        title: "Processing started",
        description: `Rotating videos ${rotationSettings.direction} by ${rotationSettings.angle}°...`
      });

      for (const fileItem of files) {
        try {
          let key = fileItem.s3Key;

          // Skip upload for imported videos that already have an S3 key
          if (!fileItem.isImported || !key) {
            // Get upload URL
            updateFileState(fileItem.id, {
              status: 'uploading',
              progress: 0,
              statusText: 'Getting upload URL...'
            });

            const uploadResponse = await fetch('/get-upload-url', {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({
                fileName: fileItem.file.name,
                fileType: fileItem.file.type,
                ephemeral: true
              })
            });

            if (!uploadResponse.ok) {
              throw new Error('Failed to get upload URL');
            }

            const uploadData = await uploadResponse.json();
            key = uploadData.key;

            // Upload to S3
            updateFileState(fileItem.id, {
              statusText: 'Uploading to S3...',
              s3Key: key
            });

            await uploadToS3(fileItem.file, uploadData.uploadUrl, (progress) => {
              updateFileState(fileItem.id, {
                progress,
                statusText: `Uploading... ${progress}%`
              });
            });

            updateFileState(fileItem.id, {
              status: 'uploaded',
              progress: 100,
              statusText: 'Upload complete, processing...'
            });
          }

          // Process video with rotation settings
          updateFileState(fileItem.id, {
            status: 'processing',
            statusText: `Rotating ${rotationSettings.direction} ${rotationSettings.angle}°...`
          });

          const processResponse = await fetch('/process-video', {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              key,
              fileName: fileItem.file.name,
              rotation: {
                direction: rotationSettings.direction,
                angle: rotationSettings.angle
              }
            })
          });

          if (!processResponse.ok) {
            throw new Error('Failed to process video');
          }

          const result = await processResponse.json();

          updateFileState(fileItem.id, { 
            status: 'processed', 
            progress: 100, 
            statusText: 'Completed!',
            downloadUrl: result.downloadUrl 
          });

        } catch (error) {
          console.error(`Error processing ${fileItem.file.name}:`, error);
          updateFileState(fileItem.id, { 
            status: 'error', 
            progress: 0, 
            statusText: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
          });
        }
      }

      const successCount = files.filter(f => f.status === 'processed').length;
      toast({
        title: "Processing complete",
        description: `Successfully processed ${successCount} out of ${files.length} videos!`,
        variant: successCount > 0 ? "default" : "destructive"
      });

    } catch (error) {
      console.error('Batch processing error:', error);
      toast({
        title: "Processing failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadVideo = async (downloadUrl: string, fileName: string) => {
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `rotated_${rotationSettings.direction}_${rotationSettings.angle}_${fileName}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download failed",
        description: "The download link may have expired",
        variant: "destructive"
      });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalSize = files.reduce((sum, file) => sum + file.file.size, 0);
  const uploadedCount = files.filter(f => f.status === 'uploaded').length;
  const processedCount = files.filter(f => f.status === 'processed').length;

  const angles: (90 | 180 | 270)[] = [90, 180, 270];

  return (
    <main
      className="mx-auto"
      style={{
        maxWidth: 1440,
        padding: '0 16px 96px',
        background: 'var(--hj-bg)',
        color: 'var(--hj-ink)',
        minHeight: 'calc(100vh - 68px)',
      }}
    >
      {/* Breadcrumb */}
      <div
        className="flex items-center gap-2.5"
        style={{ padding: '20px 0 10px', fontSize: 13 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-1.5"
          style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>Rotator</span>
      </div>

      {/* Header */}
      <header
        style={{
          padding: '10px 0 28px',
          borderBottom: '1px solid var(--hj-hairline-soft)',
          marginBottom: 28,
        }}
      >
        <div className="mono-label" style={{ marginBottom: 10 }}>Rotator</div>
        <h1
          className="font-hj-display m-0"
          style={{
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            lineHeight: 1,
            color: 'var(--hj-ink)',
            letterSpacing: '-0.01em',
          }}
        >
          Fix sideways shots.
        </h1>
        <p
          className="m-0"
          style={{
            marginTop: 12,
            color: 'var(--hj-ink)',
            fontSize: 14.5,
            maxWidth: 560,
            lineHeight: 1.5,
          }}
        >
          Drop one or more clips, pick a direction and angle, rotate.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5">
        {/* Rotation Settings */}
        <aside
          style={{
            padding: 18,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
            alignSelf: 'start',
          }}
        >
          <div className="mono-label" style={{ marginBottom: 10 }}>Rotation</div>

          <div className="mono-label" style={{ fontSize: 9, color: 'var(--hj-ink-4)', marginBottom: 6 }}>
            Direction
          </div>
          <div className="grid grid-cols-2 gap-1.5" style={{ marginBottom: 14 }}>
            {[
              { value: 'clockwise' as const, label: 'CW', Icon: RotateCw },
              { value: 'counterclockwise' as const, label: 'CCW', Icon: RotateCcw },
            ].map((d) => {
              const active = rotationSettings.direction === d.value;
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setRotationSettings((s) => ({ ...s, direction: d.value }))}
                  className="inline-flex items-center justify-center gap-1.5 transition-colors"
                  style={{
                    padding: '10px 12px',
                    borderRadius: 'var(--hj-r-sm)',
                    background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                    border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                    color: active ? 'var(--hj-blue)' : 'var(--hj-ink-2)',
                    fontSize: 12.5,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 44,
                  }}
                >
                  <d.Icon size={14} strokeWidth={1.8} />
                  {d.label}
                </button>
              );
            })}
          </div>

          <div className="mono-label" style={{ fontSize: 9, color: 'var(--hj-ink-4)', marginBottom: 6 }}>
            Angle
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {angles.map((angle) => {
              const active = rotationSettings.angle === angle;
              return (
                <button
                  key={angle}
                  type="button"
                  onClick={() => setRotationSettings((s) => ({ ...s, angle }))}
                  className="inline-flex flex-col items-center justify-center transition-colors"
                  style={{
                    padding: '10px 0',
                    borderRadius: 'var(--hj-r-sm)',
                    background: active ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
                    border: `1px solid ${active ? 'var(--hj-blue)' : 'var(--hj-hairline-soft)'}`,
                    color: active ? 'var(--hj-blue)' : 'var(--hj-ink-2)',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    minHeight: 56,
                  }}
                >
                  <span
                    className="tabular"
                    style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--hj-font-mono)', lineHeight: 1 }}
                  >
                    {angle}°
                  </span>
                  <span className="mono-label" style={{ fontSize: 9, marginTop: 2, opacity: active ? 1 : 0.7 }}>
                    degrees
                  </span>
                </button>
              );
            })}
          </div>

          {/* Summary stats — sidebar footer */}
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: '1px solid var(--hj-hairline-soft)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              fontSize: 12,
              color: 'var(--hj-ink-3)',
            }}
          >
            <div className="flex justify-between">
              <span>Files</span>
              <span className="tabular" style={{ color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                {files.length}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Processed</span>
              <span className="tabular" style={{ color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                {processedCount}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Total size</span>
              <span className="tabular" style={{ color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>
                {formatFileSize(totalSize)}
              </span>
            </div>
          </div>
        </aside>

        {/* Main workspace */}
        <section
          style={{
            padding: 22,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          {/* Drop zone */}
          <div
            {...getRootProps()}
            className="text-center cursor-pointer transition-colors"
            style={{
              padding: '40px 24px',
              borderRadius: 'var(--hj-r-md)',
              border: `1.5px dashed ${isDragActive ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`,
              background: isDragActive ? 'var(--hj-blue-tint)' : 'var(--hj-surface-2)',
              marginBottom: files.length > 0 ? 18 : 0,
            }}
          >
            <input {...getInputProps()} />
            <div
              className="mx-auto flex items-center justify-center"
              style={{
                width: 56,
                height: 56,
                borderRadius: 'var(--hj-r-sm)',
                background: 'var(--hj-surface-3)',
                color: 'var(--hj-ink-2)',
              }}
            >
              <FolderOpen size={26} strokeWidth={1.6} />
            </div>
            <p
              className="m-0"
              style={{
                marginTop: 14,
                fontSize: 15,
                fontWeight: 500,
                color: 'var(--hj-ink)',
              }}
            >
              Drop your videos here or click to browse
            </p>
            <p
              className="mono-label"
              style={{ marginTop: 8, fontSize: 9 }}
            >
              MP4 · MOV · AVI · MKV · WebM — multiple files welcome
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowLibraryPicker(true)}
            className="inline-flex items-center gap-2"
            style={{
              marginTop: 10,
              marginBottom: files.length > 0 ? 18 : 0,
              padding: '8px 12px',
              borderRadius: 'var(--hj-r-sm)',
              border: '1px solid var(--hj-hairline)',
              background: 'transparent',
              color: 'var(--hj-ink-2)',
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <FolderOpen size={14} strokeWidth={1.8} />
            Choose from your library
          </button>

          {/* File list */}
          {files.length > 0 && (
            <div className="flex flex-col" style={{ gap: 8, marginBottom: 16 }}>
              {files.map((fileItem) => {
                const isProcessingRow =
                  fileItem.status === 'processing' || fileItem.status === 'uploading';
                const isProcessed = fileItem.status === 'processed';
                const isError = fileItem.status === 'error';
                const accentColor = isProcessed
                  ? 'var(--hj-green)'
                  : isProcessingRow
                    ? 'var(--hj-blue)'
                    : isError
                      ? 'hsl(var(--destructive))'
                      : 'var(--hj-hairline-soft)';
                return (
                  <div
                    key={fileItem.id}
                    className="flex items-center gap-3"
                    style={{
                      padding: '12px 14px',
                      borderRadius: 'var(--hj-r-sm)',
                      background: 'var(--hj-surface-2)',
                      border: `1px solid ${accentColor === 'var(--hj-hairline-soft)' ? 'var(--hj-hairline-soft)' : accentColor + '88'}`,
                    }}
                  >
                    <div className="flex-shrink-0">
                      {isProcessingRow && <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--hj-yellow-ink)' }} />}
                      {isProcessed && <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />}
                      {isError && <AlertCircle className="w-4 h-4" style={{ color: 'hsl(var(--destructive))' }} />}
                      {fileItem.status === 'ready' && <UploadIcon className="w-4 h-4" style={{ color: 'var(--hj-ink-3)' }} />}
                      {fileItem.status === 'uploaded' && <UploadIcon className="w-4 h-4" style={{ color: 'var(--hj-yellow-ink)' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="truncate"
                        style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--hj-ink)' }}
                      >
                        {fileItem.file.name}
                      </div>
                      <div
                        className="tabular"
                        style={{
                          marginTop: 2,
                          fontSize: 11,
                          color: 'var(--hj-ink-3)',
                          fontFamily: 'var(--hj-font-mono)',
                        }}
                      >
                        {formatFileSize(fileItem.file.size)} · {fileItem.statusText || fileItem.status}
                      </div>
                      {fileItem.progress > 0 && !isProcessed && (
                        <Progress value={fileItem.progress} className="h-1 mt-1.5" />
                      )}
                    </div>
                    {isProcessed && fileItem.downloadUrl && (
                      <Button
                        size="sm"
                        onClick={() => downloadVideo(fileItem.downloadUrl!, fileItem.file.name)}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        Download
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(fileItem.id)}
                      aria-label="Remove"
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        background: 'transparent',
                        border: 0,
                        color: 'var(--hj-ink-3)',
                        cursor: 'pointer',
                      }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Action row */}
          <div
            className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-2.5"
            style={{
              paddingTop: 14,
              borderTop: '1px solid var(--hj-hairline-soft)',
            }}
          >
            <Button
              variant="outline"
              onClick={clearAll}
              disabled={files.length === 0 || isProcessing}
            >
              Clear all
            </Button>
            <Button
              onClick={startProcessing}
              disabled={files.length === 0 || isProcessing}
              size="lg"
            >
              {isProcessing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
              ) : (
                <>
                  <RotateCw className="w-4 h-4 mr-2" />
                  Rotate {files.length} video{files.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>

          {/* Success summary */}
          {processedCount > 0 && !isProcessing && (
            <div
              className="flex items-center gap-2.5"
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 'var(--hj-r-sm)',
                background: 'oklch(0.42 0.15 145 / 0.12)',
                border: '1px solid var(--hj-green)',
                color: 'var(--hj-ink)',
                fontSize: 13,
              }}
            >
              <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
              Processed {processedCount} of {files.length} videos. Downloads ready above.
            </div>
          )}
        </section>
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        trigger="video-tools"
      />

      <LibraryPicker
        open={showLibraryPicker}
        onClose={() => setShowLibraryPicker(false)}
        onPick={handleLibraryPick}
      />
    </main>
  );
};
